import L       from 'leaflet';
import { EventBus } from '../core/EventBus.js';
import { GameState } from '../core/GameState.js';
import { escapeHtml } from '../utils/formatting.js';

/**
 * Category colours following maritime AIS conventions:
 *   cargo (green), tanker (red), specialized (orange), express (yellow)
 * Exported for reuse by OtherShipMarker.js (other players' ships use the
 * same category-fill convention; only the outline colour differs).
 */
export const CATEGORY_COLORS = {
  cargo:       '#2ecc71',
  tanker:      '#e74c3c',
  specialized: '#e67e22',
  express:     '#f1c40f'
};
export const DEFAULT_COLOR = '#ffffff';

/** Fallback outline colour for a player who hasn't set/received a companyColor yet. */
const DEFAULT_OWNER_COLOR = '#00bcd4';

function buildShipSvg(fill, ownerStroke) {
  return `<svg viewBox="0 0 24 24" width="28" height="28" xmlns="http://www.w3.org/2000/svg">` +
    `<polygon points="12,2 19,20 12,16 5,20" fill="${fill}" stroke="${ownerStroke}" stroke-width="1.8" stroke-linejoin="round"/>` +
    `</svg>`;
}

export class ShipMarker {
  constructor(mapLayers) {
    this.mapLayers = mapLayers;
    this.markers   = new Map(); // shipId -> L.marker
  }

  /** Add or update a marker for the given ship */
  upsert(ship) {
    const latlng = [ship.position.lat, ship.position.lng];
    const category = ship.type?.category || 'cargo';
    const fill = CATEGORY_COLORS[category] || DEFAULT_COLOR;
    // This player's own chosen company colour (settings menu) — all of
    // THIS marker set's ships are the current player's own fleet.
    const ownerStroke = GameState.player?.companyColor || DEFAULT_OWNER_COLOR;
    const heading = ship.heading || 0;

    if (!this.markers.has(ship.id)) {
      const icon = L.divIcon({
        html:       `<div class="ship-arrow" style="transform:rotate(${heading}deg)">${buildShipSvg(fill, ownerStroke)}</div>`,
        className:  'ship-marker-icon',
        iconSize:   [28, 28],
        iconAnchor: [14, 14]
      });

      const marker = L.marker(latlng, { icon, zIndexOffset: 100 })
        .addTo(this.mapLayers.getLayer('ships'));

      // See OtherShipMarker.js's _upsert() for why this must be escaped —
      // Leaflet sets string tooltip content via innerHTML.
      marker.bindTooltip(escapeHtml(ship.name), { permanent: true, direction: 'top', className: 'ship-label' });

      marker.on('click', () => {
        // Always select the ship when clicking its marker
        EventBus.emit('ui:shipSelected', { ship });
        // Also open the port panel if docked
        if (ship.status === 'docked' && ship.currentPortId) {
          const port = GameState.getPort(ship.currentPortId);
          if (port) EventBus.emit('ui:portSelected', { port });
        }
      });

      this.markers.set(ship.id, marker);
    } else {
      const marker = this.markers.get(ship.id);
      marker.setLatLng(latlng);

      // Update rotation + colour (category may change if ship upgraded, etc.)
      const el = marker.getElement();
      if (el) {
        const arrow = el.querySelector('.ship-arrow');
        if (arrow) {
          arrow.style.transform = `rotate(${heading}deg)`;
          // Update SVG fill if category changed
          const poly = arrow.querySelector('polygon');
          if (poly) {
            poly.setAttribute('fill', fill);
            poly.setAttribute('stroke', ownerStroke);
          }
        }
      }

      marker.setTooltipContent(escapeHtml(ship.name));
    }
  }

  /** Remove a ship marker (e.g., after selling) */
  remove(shipId) {
    const marker = this.markers.get(shipId);
    if (marker) {
      marker.remove();
      this.markers.delete(shipId);
    }
  }
}
